'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { type EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaCarouselThumbButton } from './embla-carousel-thumb-button';
import { cn } from '~/lib/utils';
import { Button } from '../ui/button';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

type PropType<T> = {
  getIndex: (data: T) => number;
  renderSlide: (props: {
    data: T;
    type: 'main' | 'thumbnail';
    selected: boolean;
    index?: number;
  }) => React.ReactNode;
  slides: T[];
  options?: EmblaOptionsType;
  className?: string;
  variant: 'full' | 'compact';
};

export function EmblaCarousel<T>(props: PropType<T>) {
  const { slides, options } = props;

  const hasMultiple = slides.length > 1;

  const slideLimit = props.variant == 'compact' ? 4 : 6;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentThumbScroll, setCurrentThumbScroll] = useState(0);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
    active: hasMultiple,
    ...options,
  });
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    setCurrentThumbScroll(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();

    emblaMainApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaMainApi, onSelect]);

  return (
    <div
      className={cn(
        'embla',
        'mx-auto [--thumbs-slide-spacing:_1rem] sm:[--thumbs-slide-spacing:_1.5rem]',
        props.className,
      )}
    >
      <div
        className={cn(
          'embla__viewport',
          'relative overflow-hidden border-y border-border bg-white md:rounded-3xl md:border-x',
        )}
        ref={emblaMainRef}
      >
        <div
          className={cn(
            'embla__container',
            'flex touch-pan-y touch-pinch-zoom',
          )}
        >
          {slides.map((data) => (
            <div
              className={cn(
                'embla__slide',
                'min-w-0 flex-[0_0_100%] transform-gpu',
                'aspect-square w-full',
              )}
              key={props.getIndex(data)}
            >
              <div
                className={cn(
                  'embla__slide__number',
                  'relative flex aspect-square w-full select-none items-center justify-center bg-white',
                )}
              >
                {props.renderSlide({
                  index: props.getIndex(data),
                  data,
                  type: 'main',
                  selected: props.getIndex(data) === selectedIndex,
                })}
              </div>
            </div>
          ))}
        </div>
        {hasMultiple ? (
          <>
            <Button
              variant="ghost"
              size="custom"
              className="absolute left-2 top-1/2 flex size-14 -translate-y-1/2 !rounded-md bg-white/75 text-neutral-600 hover:bg-white focus-visible:bg-white"
              disabled={!emblaMainApi?.canScrollPrev()}
              onClick={() => emblaMainApi?.scrollPrev()}
            >
              <ChevronLeftIcon className="mr-1 size-12" strokeWidth={1} />
              <span className="sr-only">Previous</span>
            </Button>
            <Button
              variant="custom"
              size="custom"
              className="absolute right-2 top-1/2 flex size-14 -translate-y-1/2 !rounded-md bg-white/75 text-neutral-600 hover:bg-white focus-visible:bg-white"
              disabled={!emblaMainApi?.canScrollNext()}
              onClick={() => emblaMainApi?.scrollNext()}
            >
              <ChevronRightIcon className="ml-1 size-12" strokeWidth={1} />
              <span className="sr-only">Next</span>
            </Button>
          </>
        ) : null}
      </div>
      {hasMultiple ? (
        <div className={cn('embla-thumbs', 'mt-4')}>
          <div
            className={cn(
              'embla-thumbs__viewport',
              'relative mx-4 -mt-1 overflow-hidden py-1 md:mx-0',
              props.variant == 'compact' ? 'px-10' : 'px-20',
            )}
            ref={emblaThumbsRef}
          >
            <div
              className={cn(
                'embla-thumbs__container',
                'ml-[calc(var(--thumbs-slide-spacing)_*_-1)] flex flex-row sm:ml-[calc(var(--thumbs-slide-spacing)_*_-1)]',
                slides.length <= slideLimit ? 'justify-center' : null, // Center the thumbs if there are less than 6
              )}
            >
              {slides.map((data) => {
                const index = props.getIndex(data);
                return (
                  <EmblaCarouselThumbButton
                    key={index}
                    onClick={() => onThumbClick(index)}
                    selected={index === selectedIndex}
                    index={index}
                    variant={props.variant}
                  >
                    {props.renderSlide({
                      index,
                      data,
                      type: 'thumbnail',
                      selected: props.getIndex(data) === selectedIndex,
                    })}
                  </EmblaCarouselThumbButton>
                );
              })}
            </div>
            {slides.length > slideLimit && (
              <>
                <div
                  className={cn(
                    'absolute left-0 top-1/2 flex h-full -translate-y-1/2 !rounded-sm bg-white text-neutral-600 hover:opacity-100 focus-visible:bg-white disabled:!opacity-75',
                    props.variant == 'compact' ? 'w-7' : 'w-14',
                  )}
                >
                  <Button
                    variant="ghost"
                    size="custom"
                    className={cn(
                      'absolute left-1 top-1/2 flex h-[calc(100%_-_8px)] -translate-y-1/2 !rounded-sm bg-white text-neutral-600 hover:opacity-100 focus-visible:bg-white disabled:!opacity-75',
                      props.variant == 'compact' ? 'w-6' : 'w-12',
                    )}
                    disabled={currentThumbScroll == 0}
                    onClick={() => {
                      if (currentThumbScroll - (slideLimit + 1) < 0) {
                        emblaThumbsApi?.scrollTo(0);
                        setCurrentThumbScroll(0);
                      } else {
                        emblaThumbsApi?.scrollTo(
                          currentThumbScroll - (slideLimit + 1),
                        );
                        setCurrentThumbScroll(
                          currentThumbScroll - (slideLimit + 1),
                        );
                      }
                    }}
                  >
                    <ChevronLeftIcon className="size-12" strokeWidth={1} />
                    <span className="sr-only">Scroll Left</span>
                  </Button>
                </div>

                <div
                  className={cn(
                    'absolute right-0 top-1/2 flex h-full -translate-y-1/2 !rounded-sm bg-white text-neutral-600 focus-visible:bg-white disabled:!opacity-75',
                    props.variant == 'compact' ? 'w-7' : 'w-14',
                  )}
                >
                  <Button
                    variant="custom"
                    size="custom"
                    className={cn(
                      'absolute right-1 top-1/2 flex h-[calc(100%_-_8px)] -translate-y-1/2 !rounded-sm bg-white text-neutral-600 focus-visible:bg-white disabled:!opacity-75',
                      props.variant == 'compact' ? 'w-6' : 'w-12',
                    )}
                    disabled={currentThumbScroll == slides.length - 1}
                    onClick={() => {
                      if (
                        currentThumbScroll + (slideLimit + 1) >
                        slides.length - 1
                      ) {
                        emblaThumbsApi?.scrollTo(slides.length - 1);
                        setCurrentThumbScroll(slides.length - 1);
                      } else {
                        emblaThumbsApi?.scrollTo(
                          currentThumbScroll + (slideLimit + 1),
                        );
                        setCurrentThumbScroll(
                          currentThumbScroll + (slideLimit + 1),
                        );
                      }
                    }}
                  >
                    <ChevronRightIcon className="size-12" strokeWidth={1} />
                    <span className="sr-only">Scroll Right</span>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
